import * as XLSX from 'xlsx'

/**
 * Excel Export Utilities for Flag Statistics
 * Provides functions for downloading flag statistics as Excel files
 */

/**
 * Downloads flag statistics as an Excel file
 * @param {Object} params - Export parameters
 * @param {Array} params.flagCategories - Array of flag category definitions
 * @param {Object} params.flagStats - Statistics object with total, valid, invalid stats
 * @param {Object} params.subjectInfo - Subject information (subid, dataset_identifier)
 * @param {number} params.totalCurves - Total number of curves
 * @param {number} params.flaggedCount - Number of flagged curves
 * @param {number} params.unflaggedCount - Number of unflagged curves
 * @param {Array} params.overriddenCurveEntries - Array of manually overridden curves
 * @param {Array} params.curveFeatures - Optional: Full curve features data array
 * @returns {Promise} - Download promise
 */
export async function downloadFlagStatistics({ 
  flagCategories = [],
  flagStats = {},
  subjectInfo = {},
  totalCurves = 0,
  flaggedCount = 0,
  unflaggedCount = 0,
  overriddenCurveEntries = [],
  curveFeatures = []
}) {
  try {
    // Create a new workbook
    const workbook = XLSX.utils.book_new()

    // Sheet 1: Flag Statistics
    const statsDataArray = createFlagStatsSheet(flagCategories, flagStats, subjectInfo, totalCurves, flaggedCount, unflaggedCount, overriddenCurveEntries)
    const statsSheet = XLSX.utils.aoa_to_sheet(statsDataArray)
    XLSX.utils.book_append_sheet(workbook, statsSheet, 'Flag Statistics')

    // Sheet 2: Per-Curve Flags (if curve features provided)
    if (curveFeatures && curveFeatures.length > 0) {
      const perCurveDataArray = createPerCurveFlagsSheet(curveFeatures, subjectInfo)
      const perCurveSheet = XLSX.utils.aoa_to_sheet(perCurveDataArray)
      XLSX.utils.book_append_sheet(workbook, perCurveSheet, 'Per-Curve Flags')
    }

    // Generate filename
    const subid = subjectInfo.subid || 'Unknown'
    const datasetId = subjectInfo.dataset_identifier || '001'
    const timestamp = new Date().toISOString().slice(0, 10) // YYYY-MM-DD
    const filename = `${subid}_${datasetId}_flag_statistics_${timestamp}.xlsx`

    // Write and download the file
    XLSX.writeFile(workbook, filename)

    return { success: true, filename }
  } catch (error) {
    console.error('Flag statistics export failed:', error)
    throw error
  }
}

/**
 * Creates the flag statistics data sheet
 * @param {Array} flagCategories - Array of flag category definitions
 * @param {Object} flagStats - Statistics object with total, valid, invalid
 * @param {Object} subjectInfo - Subject information
 * @param {number} totalCurves - Total number of curves
 * @param {number} flaggedCount - Number of flagged curves
 * @param {number} unflaggedCount - Number of unflagged curves
 * @param {Array} overriddenCurveEntries - Array of manually overridden curves
 * @returns {Array} - Worksheet data array
 */
function createFlagStatsSheet(flagCategories, flagStats, subjectInfo, totalCurves, flaggedCount, unflaggedCount, overriddenCurveEntries) {
  const data = [
    ['Flag Statistics Report'],
    [''],
    ['Subject Information:'],
    ['Subject ID', subjectInfo.subid || 'N/A'],
    ['Dataset', subjectInfo.dataset_identifier || 'N/A'],
    ['Total Curves', totalCurves],
    ['Flagged Curves', flaggedCount],
    ['Unflagged Curves', unflaggedCount],
    ['']
  ]

  // Add manual overrides section if any exist
  if (overriddenCurveEntries && overriddenCurveEntries.length > 0) {
    data.push(['Manual Overrides:'])
    data.push(['Number of Overridden Curves', overriddenCurveEntries.length])
    const overrideIds = overriddenCurveEntries.map(e => e.displayId).join(', ')
    data.push(['Curve IDs', overrideIds])
    data.push([''])
  }

  // Add header row
  data.push(['Region', 'Flag Metric', 'Category', 'Mean', 'SD', 'Min', 'Max', 'Count', 'Flag Status'])

  // Add statistics for each flag metric
  flagCategories.forEach(category => {
    category.flags
      .filter(flag => !flag.isSecondaryMetric) // Only include primary metrics
      .forEach((flag, index) => {
        const metricLabel = flag.label || flag.key
        const region = category.category || 'N/A'

        // Calculate flag status (how many curves are flagged by this flag)
        const flaggedByThisFlag = flagStats.flaggedByFlag?.[flag.key] || 0
        const flagStatus = flaggedByThisFlag === 0 ? 'No Flags' : 
                          flaggedByThisFlag === totalCurves ? 'All Flagged' : 
                          `${flaggedByThisFlag} Flagged`

        // High-Quality statistics
        const validStatsObj = flagStats.valid?.[flag.key]
        const validStats = parseStatsString(validStatsObj)
        if (validStats) {
          data.push([
            index === 0 ? region : '', // Only show region on first row of category
            metricLabel,
            'High-Quality',
            validStats.mean !== null ? validStats.mean.toFixed(2) : '–',
            validStats.sd !== null ? validStats.sd.toFixed(2) : '–',
            validStats.min !== null ? validStats.min.toFixed(2) : '–',
            validStats.max !== null ? validStats.max.toFixed(2) : '–',
            validStats.count || 0,
            flagStatus
          ])
        } else {
          data.push([
            index === 0 ? region : '',
            metricLabel,
            'High-Quality',
            '–',
            '–',
            '–',
            '–',
            0,
            flagStatus
          ])
        }

        // Low-Quality statistics
        const invalidStatsObj = flagStats.invalid?.[flag.key]
        const invalidStats = parseStatsString(invalidStatsObj)
        if (invalidStats) {
          data.push([
            '',
            metricLabel,
            'Low-Quality',
            invalidStats.mean !== null ? invalidStats.mean.toFixed(2) : '–',
            invalidStats.sd !== null ? invalidStats.sd.toFixed(2) : '–',
            invalidStats.min !== null ? invalidStats.min.toFixed(2) : '–',
            invalidStats.max !== null ? invalidStats.max.toFixed(2) : '–',
            invalidStats.count || 0,
            flagStatus
          ])
        } else {
          data.push([
            '',
            metricLabel,
            'Low-Quality',
            '–',
            '–',
            '–',
            '–',
            0,
            flagStatus
          ])
        }

        // Total statistics
        const totalStatsObj = flagStats.total?.[flag.key]
        const totalStats = parseStatsString(totalStatsObj)
        if (totalStats) {
          data.push([
            '',
            metricLabel,
            'Total',
            totalStats.mean !== null ? totalStats.mean.toFixed(2) : '–',
            totalStats.sd !== null ? totalStats.sd.toFixed(2) : '–',
            totalStats.min !== null ? totalStats.min.toFixed(2) : '–',
            totalStats.max !== null ? totalStats.max.toFixed(2) : '–',
            totalStats.count || 0,
            flagStatus
          ])
        } else {
          data.push([
            '',
            metricLabel,
            'Total',
            '–',
            '–',
            '–',
            '–',
            0,
            flagStatus
          ])
        }

        // Add empty row between flags for readability
        data.push([''])
      })
  })

  // Add footer
  data.push([''])
  data.push(['Report Generated:', new Date().toLocaleString()])

  return data
}

/**
 * Parses a stats string or object
 * @param {string|Object} statsInput - Stats string or object
 * @returns {Object} - Parsed stats object with mean, min, max, sd, count
 */
function parseStatsString(statsInput) {
  if (!statsInput) return null

  // If it's already an object with the stats, use it directly
  if (typeof statsInput === 'object' && statsInput !== null) {
    return {
      mean: statsInput.mean !== undefined ? Number(statsInput.mean) : null,
      sd: statsInput.sd !== undefined ? Number(statsInput.sd) : null,
      min: statsInput.min !== undefined ? Number(statsInput.min) : null,
      max: statsInput.max !== undefined ? Number(statsInput.max) : null,
      count: statsInput.count !== undefined ? Number(statsInput.count) : 0
    }
  }

  // Otherwise, parse the string format (if any)
  if (typeof statsInput === 'string') {
    const num = Number(statsInput)
    if (!isNaN(num)) {
      return {
        mean: num,
        sd: null,
        min: null,
        max: null,
        count: null
      }
    }
  }

  return null
}

/**
 * Creates the per-curve flags data sheet
 * @param {Array} curveFeatures - Array of curve feature objects
 * @param {Object} subjectInfo - Subject information
 * @returns {Array} - Worksheet data array
 */
function createPerCurveFlagsSheet(curveFeatures, subjectInfo) {
  if (!curveFeatures || curveFeatures.length === 0) {
    return [['No curve data available']]
  }

  // Collect all unique column names from all curves
  const allColumns = new Set()
  curveFeatures.forEach(curve => {
    Object.keys(curve).forEach(col => allColumns.add(col))
  })

  // Identify flag columns (start with FLAG_)
  const flagColumns = Array.from(allColumns)
    .filter(col => col.startsWith('FLAG_'))
    .sort()

  // Identify manual override columns
  const overrideColumns = Array.from(allColumns)
    .filter(col => col === 'override_flags' || col === 'override_note')
    .sort()

  // Build column order: identifier columns first, then flags, then overrides
  const identifierColumns = ['subid', 'dataset_id', 'curve_id']
  const orderedColumns = [
    ...identifierColumns.filter(col => allColumns.has(col)),
    ...flagColumns,
    ...overrideColumns
  ]

  // Create header row
  const data = [orderedColumns]

  // Add data rows
  curveFeatures.forEach(curve => {
    const row = orderedColumns.map(col => {
      const value = curve[col]
      // Handle missing identifier columns - use subjectInfo or curve data as fallback
      if (col === 'subid' && (value === undefined || value === null)) {
        return curve.subid || subjectInfo.subid || 'N/A'
      }
      if (col === 'dataset_id' && (value === undefined || value === null)) {
        return curve.dataset_id || curve.dataset_identifier || subjectInfo.dataset_identifier || 'N/A'
      }
      if (col === 'curve_id' && (value === undefined || value === null)) {
        return curve.curve_id || 'N/A'
      }
      // For other columns, return the value or empty string
      return value !== undefined && value !== null ? value : ''
    })
    data.push(row)
  })

  return data
}

/**
 * Validates that required data is available for export
 * @param {Object} params - Export parameters
 * @returns {boolean} - True if data is valid for export
 */
export function validateFlagStatisticsData(params) {
  const { flagCategories = [], flagStats = {}, subjectInfo = {} } = params
  
  if (!flagCategories || flagCategories.length === 0) {
    console.warn('No flag categories available for export')
    return false
  }
  
  if (!subjectInfo.subid) {
    console.warn('No subject ID available for export')
    return false
  }
  
  return true
}

